import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Repeat, RefreshCw } from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import TarjetaIngresoEgreso from '../components/TarjetaIngresoEgreso';
import TarjetaDisponibilidad from '../components/TarjetaDisponibilidad';
import TarjetaPagosProximos from '../components/TarjetaPagosProximos';
import TarjetaAlertas from '../components/TarjetaAlertas';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

const COLOR_INGRESOS = '#74D5AD';
const COLOR_EGRESOS = '#FF6B68';
const COLOR_SALDO = '#D8C09A';

export default function Dashboard() {
  const navigate = useNavigate();
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;
  const [cuentas, setCuentas] = useState([]);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState('');
  const [resumen, setResumen] = useState([]);
  const [resumen30, setResumen30] = useState({ ingresos: 0, egresos: 0 });
  const [ultimosMovimientos, setUltimosMovimientos] = useState([]);
  const [tasasHoy, setTasasHoy] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    api.get('/cuentas').then((res) => {
      setCuentas(res.data);
      if (res.data.length > 0) setCuentaSeleccionada(res.data[0].id);
      else setCargando(false);
    });
    api.get('/tasas-cambio/hoy').then((res) => setTasasHoy(res.data)).catch(() => setTasasHoy(null));
  }, [empresaActiva]);

  useEffect(() => {
    if (!cuentaSeleccionada) return;
    setCargando(true);
    Promise.all([
      api.get('/movimientos/resumen-mensual', { params: { cuenta_id: cuentaSeleccionada } }),
      api.get('/movimientos/resumen-periodo', { params: { cuenta_id: cuentaSeleccionada, dias: 30 } }),
      api.get('/movimientos', { params: { cuenta_id: cuentaSeleccionada } })
    ])
      .then(([resMensual, resPeriodo, resMovimientos]) => {
        setResumen(resMensual.data);
        setResumen30(resPeriodo.data);
        setUltimosMovimientos(resMovimientos.data.datos.slice(0, 10));
      })
      .finally(() => setCargando(false));
  }, [cuentaSeleccionada]);

  const cuentaActual = cuentas.find((c) => String(c.id) === String(cuentaSeleccionada));
  const simboloMoneda = cuentaActual?.moneda === 'BS' ? 'Bs ' : '$';
  const totalIngresos6m = resumen.reduce((s, m) => s + m.ingresos, 0);
  const totalEgresos6m = resumen.reduce((s, m) => s + m.egresos, 0);

  // Saldo = acumulado de (ingresos - egresos) dentro del período mostrado,
  // no el saldo real histórico de la cuenta (que requeriría reconstruir día a día).
  let acumuladoFlujo = 0;
  const datosFlujo = resumen.map((m) => {
    acumuladoFlujo += m.ingresos - m.egresos;
    return { ...m, saldo: acumuladoFlujo };
  });

  const formatEjeY = (v) => {
    const abs = Math.abs(v);
    const signo = v < 0 ? '-' : '';
    if (abs >= 1000) return `${signo}${simboloMoneda}${Math.round(abs / 1000)}K`;
    return `${signo}${simboloMoneda}${Math.round(abs)}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Bancos
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        {cuentas.length > 0 && (
          <select
            value={cuentaSeleccionada}
            onChange={(e) => setCuentaSeleccionada(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm self-start sm:self-auto"
          >
            {cuentas.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        )}
      </div>

      {cuentas.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400">
          Aún no hay cuentas creadas en esta empresa. Ve a "Cuentas" para crear la primera.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <TarjetaDisponibilidad cuentas={cuentas} tasasHoy={tasasHoy} />
            <TarjetaPagosProximos empresaActiva={empresaActiva} />

            <div className="bg-white rounded-xl shadow-sm p-5">
              <p className="text-sm text-slate-500">Saldo actual</p>
              <p className="text-2xl font-bold text-slate-800">
                {simboloMoneda}{formatMonto(cuentaActual?.saldo_actual)}
              </p>
              {cuentaActual?.moneda === 'BS' && tasasHoy?.tasa_bcv > 0 && tasasHoy?.tasa_usd_generica > 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  BCV$ {formatMonto(cuentaActual.saldo_actual / tasasHoy.tasa_bcv)} · $ {formatMonto(cuentaActual.saldo_actual / tasasHoy.tasa_usd_generica)}
                </p>
              )}
            </div>

            <TarjetaIngresoEgreso titulo="Últimos 30 días" ingresos={resumen30.ingresos} egresos={resumen30.egresos} simbolo={simboloMoneda} />
            <TarjetaIngresoEgreso titulo="Últimos 6 meses" ingresos={totalIngresos6m} egresos={totalEgresos6m} simbolo={simboloMoneda} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4 md:col-span-2 h-[274px] overflow-hidden flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-slate-700 leading-tight">Flujo de Efectivo</h2>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Entradas y salidas · {cuentaActual?.nombre}
                  </p>
                </div>
                <LeyendaFlujo />
              </div>
              {cargando ? (
                <p className="text-slate-400 text-sm py-10 text-center flex-1">Cargando...</p>
              ) : (
                <div className="mt-2">
                  <ResponsiveContainer width="100%" height={198}>
                    <ComposedChart data={datosFlujo} margin={{ top: 14, right: 12, bottom: 4, left: 0 }} barGap={4}>
                      <CartesianGrid vertical={false} stroke="rgba(15,23,42,0.06)" />
                      <XAxis
                        dataKey="etiqueta"
                        tick={{ fontSize: 10, fill: '#89949e' }}
                        axisLine={{ stroke: 'rgba(15,23,42,0.08)' }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#89949e' }}
                        axisLine={false}
                        tickLine={false}
                        width={40}
                        tickFormatter={formatEjeY}
                      />
                      <Tooltip content={<TooltipFlujo simbolo={simboloMoneda} />} cursor={{ fill: 'rgba(15,23,42,0.04)' }} />
                      <Bar dataKey="ingresos" name="Ingresos" fill={COLOR_INGRESOS} radius={[3, 3, 0, 0]} barSize={10} />
                      <Bar dataKey="egresos" name="Egresos" fill={COLOR_EGRESOS} radius={[3, 3, 0, 0]} barSize={10} />
                      <Line
                        type="monotone"
                        dataKey="saldo"
                        name="Saldo"
                        stroke={COLOR_SALDO}
                        strokeWidth={1.4}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <TarjetaAlertas cuentas={cuentas} tasasHoy={tasasHoy} empresaActiva={empresaActiva} />

            <div className="bg-white rounded-xl shadow-sm p-4">
              <h2 className="font-semibold text-slate-700 mb-4">Accesos rápidos</h2>
              <div className="space-y-2">
                <AccesoRapido
                  icon={Plus}
                  color="bg-primary-600"
                  titulo="Nuevo movimiento"
                  subtitulo="Registrar entrada o salida"
                  onClick={() => navigate('/movimientos?accion=nuevo')}
                />
                <AccesoRapido
                  icon={Repeat}
                  color="bg-amber-500"
                  titulo="Nueva transferencia"
                  subtitulo="Entre cuentas o empresas"
                  onClick={() => navigate('/movimientos?accion=transferencia')}
                />
                <AccesoRapido
                  icon={RefreshCw}
                  color="bg-emerald-600"
                  titulo="Actualizar Tasa de Cambio"
                  subtitulo="BCV y USD del día"
                  onClick={() => navigate('/tasas-cambio')}
                />
                <AccesoRapido titulo="Por definir" subtitulo="Próximamente" disabled />
              </div>
            </div>
          </div>

          <div
            {...filaClickeable(() => navigate('/movimientos'))}
            role="button"
            className={`bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow ${FOCO_FILA}`}
          >
            <h2 className="font-semibold text-slate-700 mb-4">Últimos movimientos</h2>
            {cargando ? (
              <p className="text-slate-400 text-sm py-10 text-center">Cargando...</p>
            ) : ultimosMovimientos.length === 0 ? (
              <p className="text-slate-400 text-sm py-6 text-center">Sin movimientos registrados.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 text-left">
                    <tr>
                      <th className="p-2">Fecha</th>
                      <th className="p-2">Categoría</th>
                      <th className="p-2 text-right">Monto</th>
                      <th className="p-2">Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ultimosMovimientos.map((m) => (
                      <tr key={m.id} className="border-t hover:bg-slate-50 transition-colors">
                        <td className="p-2 whitespace-nowrap">{formatFecha(m.fecha)}</td>
                        <td className="p-2 whitespace-nowrap">{m.categoria_nombre || '-'}</td>
                        <td className={`p-2 text-right font-medium whitespace-nowrap ${m.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                          {simboloMoneda}{formatMonto(m.monto)}
                        </td>
                        <td className="p-2 max-w-[220px] truncate">{m.descripcion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function LeyendaFlujo() {
  return (
    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[11px] text-slate-500 shrink-0">
      <LeyendaItem color={COLOR_INGRESOS} label="Ingresos" />
      <LeyendaItem color={COLOR_EGRESOS} label="Egresos" />
      <LeyendaItem color={COLOR_SALDO} label="Saldo" />
    </div>
  );
}

function LeyendaItem({ color, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
      {label}
    </span>
  );
}

function TooltipFlujo({ active, payload, label, simbolo }) {
  if (!active || !payload || payload.length === 0) return null;
  const valorDe = (key) => payload.find((p) => p.dataKey === key)?.value ?? 0;
  return (
    <div
      className="text-xs shadow-lg"
      style={{
        background: '#131d26',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: 8,
        padding: '10px 11px',
        minWidth: 120,
        color: '#fff'
      }}
    >
      <p className="text-[11px] text-slate-300 mb-1.5">{label}</p>
      <TooltipFila color={COLOR_INGRESOS} label="Ingresos" valor={valorDe('ingresos')} simbolo={simbolo} />
      <TooltipFila color={COLOR_EGRESOS} label="Egresos" valor={valorDe('egresos')} simbolo={simbolo} />
      <TooltipFila color={COLOR_SALDO} label="Saldo" valor={valorDe('saldo')} simbolo={simbolo} />
    </div>
  );
}

function TooltipFila({ color, label, valor, simbolo }) {
  return (
    <div className="flex items-center justify-between gap-3 leading-5">
      <span className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: color }} />
        {label}
      </span>
      <span className="font-medium">{simbolo}{formatMonto(valor)}</span>
    </div>
  );
}

function AccesoRapido({ icon: Icon, color, titulo, subtitulo, onClick, disabled }) {
  if (disabled) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-dashed border-slate-200 p-2.5 text-slate-300 select-none">
        <div className="w-9 h-9 rounded-full border-2 border-dashed border-slate-200 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium">{titulo}</p>
          <p className="text-xs">{subtitulo}</p>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 rounded-lg border border-slate-200 p-2.5 text-left hover:border-primary-300 hover:bg-primary-50 transition-colors"
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0 ${color}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-sm font-medium text-slate-800">{titulo}</p>
        <p className="text-xs text-slate-400">{subtitulo}</p>
      </div>
    </button>
  );
}
